import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { getCurrentMemberId } from "./roster";
import { EntityPicker } from "./EntityPicker";
import { SetForCampaignPicker, type CampaignAssignment } from "./SetForCampaignPicker";
import { splitAssets, replaceAsset } from "./assetSegments";
import { AssetDocument, rendererFor } from "./assetRenderers";

/**
 * Shared slide-out drawer for My Tasks (AMB-367 / D2). Opening a task's "See draft" slides
 * in its AI draft on the right (wide, like the v8 Reviews drawer / the signed-off prototype),
 * with the loop's inline actions: Send for Review, Set for Campaign, Mark done — plus
 * Go-to-meeting and Open-in-Knowledge-Base for fuller context on demand, and prev/next
 * cycling through the meeting's other drafts so you can triage without losing your place.
 */

export interface DrawerTask {
  id: string;
  task: string;
  meetingId: string | null;
  meetingName?: string;
  meetingDate?: string | null;
  doc?: { id: string; title: string };
}

interface Props {
  task: DrawerTask;
  /** Drafted tasks in the same view, for prev/next cycling. */
  draftTasks: DrawerTask[];
  onClose: () => void;
  onNavigate: (taskId: string) => void;
  onMarkDone: (taskId: string) => void;
}

export function TaskDrawer({ task, draftTasks, onClose, onNavigate, onMarkDone }: Props) {
  // Use the agency "all" scope for the deliverable read + writes: a task's draft can live
  // under any client (or the brief folder), and the operator API resolves a deliverable by
  // its unique id across every client under "all". Scoping to the active client would let
  // the read (which searches the union) succeed while the write (per-client) silently 404s.
  const slug = "all";
  const [body, setBody] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendOpen, setSendOpen] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [sendSir, setSendSir] = useState({ situation: "", impact: "", statement: "" });
  const [flash, setFlash] = useState<string | null>(null);
  const showFlash = (m: string) => { setFlash(m); window.setTimeout(() => setFlash(null), 2600); };
  // AMB-418: inline editing — edit the draft + save without leaving the drawer.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");            // legacy (un-tagged) whole-body edit
  const [assetDrafts, setAssetDrafts] = useState<string[]>([]); // per-asset edit when tagged
  const [saving, setSaving] = useState(false);

  const docId = task.doc?.id;
  const idx = draftTasks.findIndex((t) => t.id === task.id);
  // Parse <asset> tags so the drawer renders + edits per-asset (same as the asset page).
  const parsed = useMemo(() => splitAssets(body || ""), [body]);

  useEffect(() => {
    setEditing(false); // leaving edit mode when the draft changes (prev/next)
    if (!docId) { setBody(null); setLoading(false); return; }
    setLoading(true);
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/${encodeURIComponent(docId)}?client=${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setBody(d.body || d.content || ""))
      .catch(() => setBody(""))
      .finally(() => setLoading(false));
  }, [docId, slug]);

  // Returns whether the write actually succeeded, so the caller can confirm or warn (a
  // failed write used to be swallowed silently — that's how a "send" could vanish).
  const update = (fields: Record<string, unknown>): Promise<boolean> => {
    if (!docId) return Promise.resolve(false);
    return fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: slug, id: docId, fields }),
    }).then((r) => r.ok).catch(() => false);
  };

  const startEdit = () => {
    if (parsed.hasAssets) setAssetDrafts(parsed.assets.map((a) => a.content));
    else setDraft(body || "");
    setEditing(true);
  };
  // Save via the content endpoint. Tagged → splice each edited asset back into the original
  // body (scaffolding preserved); legacy → save the whole textarea.
  const saveEdit = () => {
    if (!docId) return;
    setSaving(true);
    let next = body || "";
    if (parsed.hasAssets) assetDrafts.forEach((c, i) => { next = replaceAsset(next, i, c); });
    else next = draft;
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/content`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: slug, id: docId, body: next }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        setBody(next);
        setEditing(false);
        showFlash("Saved");
      })
      .catch(() => showFlash("Couldn't save — try again"))
      .finally(() => setSaving(false));
  };

  const confirmSend = (reviewerIds: string[]) => {
    if (reviewerIds.length === 0) return;
    const fields: Record<string, unknown> = { reviewState: "in-review", reviewer: reviewerIds, submittedBy: getCurrentMemberId() };
    const hasSir = sendSir.situation.trim() || sendSir.impact.trim() || sendSir.statement.trim();
    if (hasSir) fields.sir = { situation: sendSir.situation.trim(), impact: sendSir.impact.trim(), statement: sendSir.statement.trim() };
    update(fields).then((ok) => showFlash(ok ? "Sent for review" : "Couldn't send — try again"));
    setSendOpen(false);
    setSendSir({ situation: "", impact: "", statement: "" });
  };

  const confirmCampaign = (a: CampaignAssignment) => {
    update({ campaign: a.campaign, phase: a.phase, due: a.due }).then((ok) => showFlash(ok ? "Added to campaign" : "Couldn't save — try again"));
    setCampaignOpen(false);
  };

  const sirField = (key: keyof typeof sendSir, ph: string) => (
    <input
      value={sendSir[key]}
      onChange={(e) => setSendSir((s) => ({ ...s, [key]: e.target.value }))}
      placeholder={ph}
      className="w-full rounded-lg border border-black/[0.06] px-3 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
    />
  );

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed right-0 top-0 z-50 flex h-full w-[56vw] min-w-[560px] max-w-[820px] flex-col border-l border-black/[0.06] bg-white shadow-2xl">
        {/* Header: prev/next + context links + close */}
        <div className="flex items-center justify-between border-b border-black/[0.06] px-5 py-3">
          <div className="flex items-center gap-2">
            <button disabled={idx <= 0} onClick={() => onNavigate(draftTasks[idx - 1].id)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-50 disabled:opacity-30" title="Previous draft">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
            </button>
            <span className="text-[11px] text-gray-400">{idx >= 0 ? idx + 1 : "–"} / {draftTasks.length}</span>
            <button disabled={idx < 0 || idx >= draftTasks.length - 1} onClick={() => onNavigate(draftTasks[idx + 1].id)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-50 disabled:opacity-30" title="Next draft">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
            </button>
          </div>
          <div className="flex items-center gap-1">
            {task.meetingId && (
              <Link to={`/agents/digest?id=${encodeURIComponent(task.meetingId)}&task=${encodeURIComponent(task.id)}`} className="rounded-lg px-2.5 py-1 text-[12px] font-medium text-gray-500 hover:bg-gray-50">Go to meeting</Link>
            )}
            {docId && (
              <Link to={`/agents/transcript-view?id=${encodeURIComponent(docId)}`} className="rounded-lg px-2.5 py-1 text-[12px] font-medium text-gray-700 hover:bg-gray-50">Open in Knowledge Base</Link>
            )}
            <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-50">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* Body: the draft + light source context */}
        <div className="flex-1 overflow-auto px-6 py-4">
          <h2 className="mb-1 text-[15px] font-bold text-gray-900">{task.task}</h2>
          {task.meetingName && <p className="mb-4 text-[11px] text-gray-400">from {task.meetingName}{task.meetingDate ? ` · ${task.meetingDate}` : ""}</p>}
          {loading ? (
            <p className="text-[13px] text-gray-400">Loading draft…</p>
          ) : editing && parsed.hasAssets ? (
            // Asset-only editing: one typed editor per asset; scaffolding stays as written.
            <div className="space-y-3">
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-400 ring-1 ring-black/[0.04]">
                You're editing just the asset{parsed.assets.length > 1 ? "s" : ""}. The instructions stay as written.
              </div>
              {parsed.assets.map((a, i) => {
                const Editor = rendererFor(a.type).Editor;
                return (
                  <Editor key={i} content={assetDrafts[i] ?? ""} onChange={(v) => setAssetDrafts((prev) => prev.map((c, idx) => (idx === i ? v : c)))} label={a.label || `Asset ${i + 1}`} type={a.type} />
                );
              })}
            </div>
          ) : editing ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
              className="h-[60vh] w-full resize-y rounded-lg border border-black/[0.08] bg-white p-4 font-mono text-[12px] leading-relaxed text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          ) : body ? (
            // AssetDocument: scaffolding as markdown + each <asset> via its typed renderer
            // (no raw <asset> tags or unparsed markdown leaking into the view).
            <AssetDocument body={body} />
          ) : (
            <p className="text-[13px] text-gray-400">No draft for this task. Use “Go to meeting” for context.</p>
          )}
        </div>

        {/* Footer: inline loop actions (AMB-418: Edit / Save inline) */}
        <div className="flex items-center gap-2 border-t border-black/[0.06] px-6 py-3">
          {editing ? (
            <>
              <button onClick={saveEdit} disabled={saving} className="rounded-lg bg-gray-900 px-3 py-2 text-[12px] font-semibold text-white hover:bg-gray-800 disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
              <button onClick={() => setEditing(false)} disabled={saving} className="rounded-lg px-3 py-2 text-[12px] font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
            </>
          ) : (
            <>
              {docId && (
                <button onClick={startEdit} className="rounded-lg border border-black/[0.08] px-3 py-2 text-[12px] font-medium text-gray-700 hover:bg-gray-50">Edit</button>
              )}
              {docId && (
                <button onClick={() => setSendOpen(true)} className="rounded-lg bg-gray-900 px-3 py-2 text-[12px] font-semibold text-white hover:bg-gray-800">Send for Review</button>
              )}
              {docId && (
                <button onClick={() => setCampaignOpen(true)} className="rounded-lg border border-black/[0.08] px-3 py-2 text-[12px] font-medium text-gray-700 hover:bg-gray-50">+ Add to campaign</button>
              )}
              <button onClick={() => { onMarkDone(task.id); onClose(); }} className="ml-auto rounded-lg px-3 py-2 text-[12px] font-medium text-gray-500 hover:bg-gray-50">Mark done</button>
            </>
          )}
        </div>
        {flash && (
          <div className="pointer-events-none absolute bottom-20 left-1/2 z-10 -translate-x-1/2 rounded-lg bg-gray-900 px-4 py-2 text-[12px] font-medium text-white shadow-xl">{flash}</div>
        )}
      </div>

      {sendOpen && (
        <EntityPicker
          title="Send for review — pick reviewer(s)"
          endpoint="/members"
          multiple
          mapItem={(m) => ({ id: m.id, label: m.name, sublabel: m.login ? undefined : "contact" })}
          confirmLabel="Send for review"
          onConfirm={confirmSend}
          onClose={() => { setSendOpen(false); setSendSir({ situation: "", impact: "", statement: "" }); }}
          extraTop={
            <div className="space-y-2">
              <p className="text-[11px] font-medium text-gray-400">Add context for the reviewer (optional)</p>
              {sirField("situation", "Situation — what's the background?")}
              {sirField("impact", "Impact — why does it matter?")}
              {sirField("statement", "A note to prepend before the asset")}
            </div>
          }
        />
      )}
      {campaignOpen && (
        <SetForCampaignPicker onConfirm={confirmCampaign} onClose={() => setCampaignOpen(false)} />
      )}
    </>
  );
}
